import { ChangeDetectionStrategy, Component, inject, linkedSignal } from '@angular/core';
import { form } from '@angular/forms/signals';
import { MultiSelectV3Component, SelectOption, SelectV3Component } from '@gea/digital-ui-lib';
import { SpinnerComponent } from '@shared/components';
import { FaultsStore, MachinesStore } from '@shared/state';
import { TranslatePipe } from '@ngx-translate/core';
import { FaultStatus } from '@brc/shared/models';

@Component({
  selector: 'gea-hrt-fault-filters',
  templateUrl: './fault-analysis-filters.component.html',
  styleUrls: ['./fault-analysis-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SelectV3Component, MultiSelectV3Component, SpinnerComponent, TranslatePipe],
})
export class FaultAnalysisFiltersComponent {
  private readonly faultsStore = inject(FaultsStore);

  FaultStatus = FaultStatus;

  readonly machineOptions = inject(MachinesStore).machines;
  readonly faultCodeGroups = this.faultsStore.faultCodeGroups;

  readonly selectedMachines = this.faultsStore.selectedMachines;
  private readonly selectedFaults = this.faultsStore.selectedFaults;
  private readonly selectedDateRange = this.faultsStore.selectedDateRange;
  private readonly selectedFaultStatus = this.faultsStore.selectedFaultStatus;

  readonly formValues = linkedSignal(() => ({
    machines: (this.selectedMachines() ?? []).map((value) => ({ value })) as SelectOption<number>[],
    faultCodes: (this.selectedFaults() ?? []).map((value) => ({ value })) as SelectOption<number>[],
    dateRange: [{ value: this.selectedDateRange() ?? 0 }] as SelectOption<number>[],
    status: (this.selectedFaultStatus() ?? []).map((value) => ({ value })) as SelectOption<FaultStatus>[],
  }));
  readonly formGroup = form(this.formValues);

  EMPTY_SELECT_OPTIONS: SelectOption[] = [];

  sortDateRange(options: SelectOption[]): SelectOption[] {
    return options.sort((a, b) => a.value - b.value);
  }

  clearSelectedMachines(): void {
    this.formGroup.machines().value.set([]);
    this.faultsStore.setSelectedFaultAnalysisState({ selectedMachines: [] });
  }

  onSetSelectedMachines(machines?: SelectOption<number>[]): void {
    this.formGroup.machines().value.set(machines ?? []);
    const selectedMachines = machines?.map((option) => option.value) ?? [];
    this.faultsStore.setSelectedFaultAnalysisState({ selectedMachines });
  }

  onSetSelectedFaultCodes(faultCodes?: SelectOption<number>[]): void {
    this.formGroup.faultCodes().value.set(faultCodes ?? []);
    const selectedFaults = faultCodes?.map((option) => option.value) ?? [];
    this.faultsStore.setSelectedFaultAnalysisState({ selectedFaults });
  }

  onSetDateRange(selectedDateRange?: SelectOption<number | null | undefined>): void {
    const dateRange = selectedDateRange?.value ?? 0;
    this.formGroup.dateRange().value.set([{ value: dateRange }]);
    this.faultsStore.setSelectedFaultAnalysisState({ selectedDateRange: dateRange });
  }

  onSetStatus(status?: SelectOption<FaultStatus>[]): void {
    this.formGroup.status().value.set(status ?? []);
    const selectedFaultStatus = status?.map((option) => option.value) ?? [];
    this.faultsStore.setSelectedFaultAnalysisState({ selectedFaultStatus });
  }
}
